import React from 'react';
import { Icon } from '../../../StockControl';
import { stockButtonTones } from '../stockUi';
import { getProfilePhotoUploadErrorMessage } from '../../../core/media/profilePhotoUpload';

const ControllerAvatar = ({ photoURL = '', displayName = 'Controlador' }) => {
  const initials = String(displayName || 'Controlador')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'CT';

  if (photoURL) {
    return (
      <img
        src={photoURL}
        alt={displayName}
        className="h-14 w-14 rounded-[18px] object-cover ring-1 ring-white shadow-sm"
      />
    );
  }

  return (
    <div className="flex h-14 w-14 items-center justify-center rounded-[18px] bg-[linear-gradient(135deg,#0f172a_0%,#0891b2_100%)] text-sm font-black tracking-[0.18em] text-white shadow-sm">
      {initials}
    </div>
  );
};

const StockControllersPanel = ({
  controllers,
  form,
  setForm,
  editingControllerId,
  companyId,
  uploadPhoto,
  showMessage,
  onCreate,
  onEdit,
  onCancelEdit,
  onToggle,
}) => {
  const [isUploadingPhoto, setIsUploadingPhoto] = React.useState(false);
  const photoInputRef = React.useRef(null);
  const activeControllers = controllers.filter((item) => item.active !== false);
  const copyAccess = async (value = '') => {
    if (!value || typeof navigator === 'undefined' || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      // silencio: la copia es ayuda, no flujo crítico
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file || typeof uploadPhoto !== 'function') return;

    setIsUploadingPhoto(true);
    try {
      const displayName = [form.firstName, form.lastName].filter(Boolean).join(' ');
      const photoPayload = await uploadPhoto({
        file,
        companyId,
        entityType: 'controllers',
        entityId: displayName || 'controller',
      });

      setForm((prev) => ({
        ...prev,
        photoURL: photoPayload.photoURL,
        photoStoragePath: photoPayload.storagePath,
      }));

      showMessage?.(
        photoPayload.compressionApplied
          ? 'Foto 4x4 del controlador subida y comprimida.'
          : 'Foto 4x4 del controlador subida.',
        'success'
      );
    } catch (error) {
      showMessage?.(getProfilePhotoUploadErrorMessage(error), 'error');
    } finally {
      if (event?.target) {
        event.target.value = '';
      }
      setIsUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = () => {
    setForm((prev) => ({
      ...prev,
      photoURL: '',
      photoStoragePath: '',
    }));
    showMessage?.('La foto se quitó de la ficha. Guardá los cambios para aplicarlo.', 'info');
  };

  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-600">Controladores</p>
            <h3 className="mt-1 text-xl font-bold text-slate-900">Equipo de control físico</h3>
            <p className="mt-1 text-sm text-slate-500">
              {editingControllerId
                ? 'Actualizá la ficha del controlador desde acá. Podés cambiar sus datos, el PIN y subir o reemplazar su foto.'
                : 'Creá la ficha del controlador desde acá. Más adelante esta misma identidad alimenta acceso móvil, asignación y trazabilidad.'}
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-right">
            <p className="text-xs font-medium text-slate-500">Activos</p>
            <p className="text-2xl font-bold text-slate-900">{activeControllers.length}</p>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePhotoUpload}
          />
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nombre</span>
            <input
              value={form.firstName}
              onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              placeholder="Juan"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Apellido</span>
            <input
              value={form.lastName}
              onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              placeholder="Pérez"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Celular</span>
            <input
              value={form.phone}
              onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              placeholder="Opcional"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">PIN de acceso</span>
            <input
              value={form.pin}
              onChange={(e) => setForm((prev) => ({ ...prev, pin: e.target.value }))}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:bg-white"
              placeholder="4 a 6 dígitos"
            />
          </label>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 md:col-span-2">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <ControllerAvatar
                  photoURL={form.photoURL}
                  displayName={[form.firstName, form.lastName].filter(Boolean).join(' ') || 'Controlador'}
                />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Foto 4x4 del controlador</p>
                  <p className="text-xs text-slate-500">Se comprime antes de subir para no arrastrar peso innecesario.</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {form.photoURL ? (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    disabled={isUploadingPhoto}
                    className="inline-flex min-h-[44px] items-center justify-center rounded-2xl border border-rose-200 bg-rose-50 px-4 text-sm font-semibold text-rose-700 disabled:opacity-60"
                  >
                    Eliminar foto
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={isUploadingPhoto}
                  className="inline-flex min-h-[44px] items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 disabled:opacity-60"
                >
                  {isUploadingPhoto ? 'Subiendo foto...' : form.photoURL ? 'Cambiar foto' : 'Subir foto'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 rounded-2xl border border-cyan-100 bg-cyan-50/80 px-4 py-3 text-sm text-cyan-900">
          <p className="font-semibold">{editingControllerId ? 'Qué actualizás en esta ficha' : 'Qué se genera al crear el controlador'}</p>
          <p className="mt-1 text-cyan-800">
            {editingControllerId
              ? 'Se guardan nombre, apellido, celular, PIN, estado de acceso y foto para que la trazabilidad use siempre la identidad correcta.'
              : 'Se guarda su PIN y queda lista una ruta individual para su futura vista de control desde celular.'}
          </p>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
              className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
            />
            Acceso activo
          </label>
          <div className="flex flex-wrap items-center gap-2">
            {editingControllerId ? (
              <button
                type="button"
                onClick={onCancelEdit}
                className={stockButtonTones.ghost}
              >
                <Icon path="M6 18L18 6M6 6l12 12" className="h-5 w-5" />
                Cancelar edición
              </button>
            ) : null}
            <button
              onClick={onCreate}
              className={stockButtonTones.primary}
            >
              <Icon path={editingControllerId ? 'M5.25 12.75l4.5 4.5 9-13.5' : 'M12 4.5v15m7.5-7.5h-15'} className="h-5 w-5" />
              {editingControllerId ? 'Guardar cambios' : 'Crear controlador'}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Listado</p>
            <h4 className="mt-1 text-lg font-bold text-slate-900">Controladores cargados</h4>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{controllers.length} registrados</span>
        </div>

        <div className="mt-4 space-y-3">
          {controllers.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              Todavía no hay controladores cargados.
            </div>
          ) : controllers.map((controller) => {
            const displayName = [controller.firstName, controller.lastName].filter(Boolean).join(' ');
            return (
              <div key={controller.id} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3">
                  <ControllerAvatar photoURL={controller.photoURL} displayName={displayName || 'Controlador'} />
                  <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-base font-semibold text-slate-900">{displayName || 'Sin nombre'}</p>
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${controller.active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                      {controller.active !== false ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                    {controller.phone ? <span>{controller.phone}</span> : null}
                    {controller.pin ? <span>PIN • {controller.pin}</span> : null}
                  </div>
                  {controller.accessUrl ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <a
                        href={controller.accessUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex max-w-full items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-800 hover:bg-cyan-100"
                      >
                        Link controlador
                      </a>
                      <button
                        onClick={() => copyAccess(controller.accessUrl)}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        Copiar link
                      </button>
                    </div>
                  ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onEdit?.(controller.id)}
                  className={stockButtonTones.ghost}
                >
                  <Icon path="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" className="h-4 w-4" />
                  Editar
                </button>
                <button
                  onClick={() => onToggle(controller)}
                  className={controller.active !== false ? stockButtonTones.ghost : stockButtonTones.cyan}
                >
                  <Icon path={controller.active !== false ? 'M18 12H6' : 'M12 4.5v15m7.5-7.5h-15'} className="h-4 w-4" />
                  {controller.active !== false ? 'Desactivar' : 'Reactivar'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StockControllersPanel;
